import { useQuery } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useAuthStore } from '@/stores/auth';

type RedemptionStatus = {
  in_redemption: boolean;
  ends_at?: string;
  remaining_ms?: number;
  current_count?: number;
  threshold?: number;
};

export type RedemptionLeadCount = {
  count: number;
  threshold: number;
  remainingMs: number;
  expired: boolean;
  endsAt: string;
};

function toLeadCount(data: RedemptionStatus): RedemptionLeadCount {
  const remainingMs = data.remaining_ms ?? 0;
  return {
    count: data.current_count ?? 0,
    threshold: data.threshold ?? 99,
    remainingMs,
    expired: remainingMs <= 0,
    endsAt: data.ends_at ?? '',
  };
}

// Fase 28 (28.6.3): live count + remaining time inside the 24h redemption
// window. Mirrors use-test-lead-count's polling cadence (30s) and disabled
// gating. Backed by GET /api/team-members/redemption/status which already
// does the SQL count + window math, so the frontend just renders.
//
// The status payload is the source of truth for `ends_at` — auth.user has
// the field declared but auth-mapping doesn't populate it on every screen
// load, and the polled value also refreshes if an admin extends the window.
export function useRedemptionLeadCount(): RedemptionLeadCount | null {
  const user = useAuthStore((s) => s.user);
  const enabled = user?.account_status === 'redemption_test';

  const { data } = useQuery({
    queryKey: ['redemption-status', user?.id],
    queryFn: (): Promise<RedemptionStatus> =>
      pb.send('/api/team-members/redemption/status', { method: 'GET' }),
    enabled,
    refetchInterval: 30_000,
  });

  if (!enabled || !data || !data.in_redemption) return null;
  return toLeadCount(data);
}
